/**
 * @description Hook utilizado para as operações referentes à seção de gerenciamento dos dados da Balística
 * @author Diego Sabino Amorim de Araújo
 * @file useGerenciamentoBalisticaService.js
 * @license Copyright (c) 2022 SIGEP DIGETI ITEP/RN
 */

import actionTypes from 'utils/actionTypes'
import { useDispatch } from 'react-redux'
import { setProcessando } from 'actions'
import { useFetch } from './useFetch'

export const useGerenciamentoBalisticaService = () => {
  const fetch = useFetch()
  const dispatch = useDispatch()

  const setModeloBalisticoGlobal = async (modelo) => {
    dispatch({
      type: actionTypes.MODELO_BALISTICO,
      modeloBalistico: modelo,
    })
  }

  const listarDadosItensBalisticosGlobal = async (modelo) => {
    try {
      dispatch(setProcessando(true))
      const response = await fetch.get(`/gerenciamentoBalistica/${modelo}`)
      dispatch({
        type: actionTypes.FETCH_ITENS_BALISTICOS_GERENCIAVEIS,
        itensBalisticos: response.data.itens,
      })
    } catch (err) {
      return
    } finally {
      dispatch(setProcessando(false))
    }
  }

  const adicionarItemGerenciamentoBalistico = async (nome, modeloBalistico) => {
    try {
      dispatch(setProcessando(true))
      await fetch.postOpen('/gerenciamentoBalistica', {
        modelo: modeloBalistico,
        nome,
      })
    } catch (err) {
      return
    } finally {
      dispatch(setProcessando(false))
    }
  }

  const deletarItemGerenciamentoBalistico = async (id, modeloBalistico) => {
    try {
      dispatch(setProcessando(true))

      await fetch.del(`/gerenciamentoBalistica/${id}/`, { modelo: modeloBalistico })
    } catch (err) {
      return
    } finally {
      dispatch(setProcessando(false))
    }
  }

  const listarOpcoesModeloBalistico = async (modelo, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/gerenciamentoBalistica/${modelo}`)
      dispatch({
        type: actionTypes[modelo],
        opcoes: response.data.itens,
      })
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarOpcoesClassificacaoArmaDeFogoGlobal = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await Promise.all([
        listarOpcoesModeloBalistico(actionTypes.TIPO_ARMA_DE_FOGO),
        listarOpcoesModeloBalistico(actionTypes.MARCA_ARMA_DE_FOGO),
        listarOpcoesModeloBalistico(actionTypes.LOCAL_FABRICACAO_ARMA_DE_FOGO),
        listarOpcoesModeloBalistico(actionTypes.MODELO_ARMA_DE_FOGO),
        listarOpcoesModeloBalistico(actionTypes.CALIBRE_NOMINAL_ARMA_DE_FOGO),
        listarOpcoesModeloBalistico(actionTypes.CALIBRE_NOMINAL_CARTUCHO),
        listarOpcoesModeloBalistico(actionTypes.CALIBRE_CARTUCHO_VARIACAO),
        listarOpcoesModeloBalistico(actionTypes.NUMERACAO_DE_SERIE_ARMA_DE_FOGO),
        listarOpcoesModeloBalistico(actionTypes.LOCAL_GRAVACAO_NUMERACAO_DE_SERIE_ARMA_DE_FOGO),
        listarOpcoesModeloBalistico(actionTypes.NUMERACAO_DE_MONTAGEM_ARMA_DE_FOGO),
        listarOpcoesModeloBalistico(actionTypes.LOCAL_GRAVACAO_NUMERACAO_DE_MONTAGEM_ARMA_DE_FOGO),
        listarOpcoesModeloBalistico(actionTypes.TIPO_ACABAMENTO_ARMA_DE_FOGO),
        listarOpcoesModeloBalistico(actionTypes.ESTADO_CONSERVACAO_ARMA_DE_FOGO),
        listarOpcoesModeloBalistico(actionTypes.SISTEMA_PERCUSSAO_ARMA_DE_FOGO),
        listarOpcoesModeloBalistico(actionTypes.TIPO_ALMA_DO_CANO_ARMA_DE_FOGO),
        listarOpcoesModeloBalistico(actionTypes.SISTEMA_CARREGAMENTO_ARMA_DE_FOGO),
        listarOpcoesModeloBalistico(actionTypes.TIPO_TAMBOR_ARMA_DE_FOGO),
        listarOpcoesModeloBalistico(actionTypes.TIPO_CARREGADOR_ARMA_DE_FOGO),
        listarOpcoesModeloBalistico(actionTypes.SISTEMA_FUNCIONAMENTO_ARMA_DE_FOGO),
        listarOpcoesModeloBalistico(actionTypes.TIPO_MECANISMO_DISPARO_ARMA_DE_FOGO),
        listarOpcoesModeloBalistico(actionTypes.TIPO_MASSA_DE_MIRA_ARMA_DE_FOGO),
        listarOpcoesModeloBalistico(actionTypes.TIPO_ALCA_DE_MIRA_ARMA_DE_FOGO),
        listarOpcoesModeloBalistico(actionTypes.TIPO_EMPUNHADURA_ARMA_DE_FOGO),
        listarOpcoesModeloBalistico(actionTypes.TIPO_CORONHA_ARMA_DE_FOGO),
        listarOpcoesModeloBalistico(actionTypes.TIPO_TELHA_ARMA_DE_FOGO),
        listarOpcoesModeloBalistico(actionTypes.TIPO_GUARDA_MAO_ARMA_DE_FOGO),
        listarOpcoesModeloBalistico(actionTypes.TIPO_GRAVACAO_ARMA_DE_FOGO),
      ])
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarOpcoesClassificacaoCartuchoGlobal = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)

      await Promise.all([
        listarOpcoesModeloBalistico(actionTypes.CALIBRE_NOMINAL_CARTUCHO),
        listarOpcoesModeloBalistico(actionTypes.FABRICANTE_CARTUCHO),
        listarOpcoesModeloBalistico(actionTypes.TIPO_ESTOJO_CARTUCHO),
        listarOpcoesModeloBalistico(actionTypes.TIPO_PROJETIL_CARTUCHO),
        listarOpcoesModeloBalistico(actionTypes.NUMERO_LOTE_CARTUCHO),
        listarOpcoesModeloBalistico(actionTypes.CARACTERISTICA_CARTUCHO),
      ])
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarOpcoesClassificacaoElementoMunicaoGlobal = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)

      await Promise.all([
        listarOpcoesModeloBalistico(actionTypes.CALIBRE_COMPATIVEL_ELEMENTO_MUNICAO),
        listarOpcoesModeloBalistico(actionTypes.ESPECIE_ELEMENTO_MUNICAO),
        listarOpcoesModeloBalistico(actionTypes.RESSALTOS_E_CAVADOS_ELEMENTO_MUNICAO),
        listarOpcoesModeloBalistico(actionTypes.TIPO_PROJETIL_CARTUCHO),
      ])
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  return {
    setModeloBalisticoGlobal,
    listarDadosItensBalisticosGlobal,
    adicionarItemGerenciamentoBalistico,
    deletarItemGerenciamentoBalistico,
    listarOpcoesClassificacaoArmaDeFogoGlobal,
    listarOpcoesClassificacaoCartuchoGlobal,
    listarOpcoesClassificacaoElementoMunicaoGlobal,
    listarOpcoesModeloBalistico,
  }
}
